import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Fab,
  TextField,
} from "@material-ui/core";
import { Menu, ArrowDropUpSharp } from "@material-ui/icons";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import MyLocationIcon from "@material-ui/icons/MyLocation";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AedIcon from "../../aedIcon";

const useStyles = makeStyles((theme) => ({
  toolsBarLeft: {
    display: "flex",
    justifyContent: "left",
    flexDirection: "column",
    position: "absolute",
    left: 0,
    bottom: 0,
    zIndex: 100,
    height: "auto",
  },
  toolsBarRight: {
    display: "flex",
    justifyContent: "right",
    flexDirection: "column",
    position: "absolute",
    right: 0,
    bottom: 0,
    zIndex: 100,
    height: "auto",
    pointerEvents: "none",
  },
  toolsBarTop: {
    position: "absolute",
    top: 0,
    paddingTop: theme.spacing(2),
    zIndex: 100,
    width: "100%",
    height: "auto",
    display: "flex",
    justifyContent: "center",
  },
  roomControl: { textAlign: "left", padding: theme.spacing(2) },
  zoomControl: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    position: "absolute",
    bottom: "90px",
    right: "16px",
    gap: "8px",
    pointerEvents: "auto",
  },
  campusControl: {
    textAlign: "right",
    padding: theme.spacing(2),
    pointerEvents: "auto",
  },
  select: {
    padding: theme.spacing(2),
    backgroundColor: "white",
    "&:focus": { backgroundColor: "white", borderRadius: 4 },
  },
  paper: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    width: "100%",
  },
  building_name_tag: { fontSize: 11 },
  floor_name_tag: { fontSize: 11, paddingLeft: 4 },
  room_name_tag: { fontSize: 14, fontWeight: 700 },
  list: { width: 250 },
  formControlLabel: {
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
}));

function MapToolbar({
  listArray,
  handleClick,
  handleZoomIn,
  handleZoomOut,
  handleCenterOnGps,
  clearSearchPin,
  changeCampus,
  currentCampus,
  children,
  useGps,
}) {
  const classes = useStyles();
  const [dataJson, setDataJson] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [checked, setChecked] = useState(true);
  const [searchValue, setSearchValue] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const data = await import(`../../json/campus_data.json`);
      setDataJson(data.default.campusList);
    }
    fetchData();
  }, []);

  useEffect(() => {
    const aedElement = document.querySelector("#Aed, #aed");
    if (aedElement) {
      checked
        ? aedElement.classList.remove("hidden")
        : aedElement.classList.add("hidden");
    }
  }, [checked]);

  useEffect(() => {
    setChecked(true);
    setSearchValue(null);
  }, [currentCampus]);

  if (dataJson.length === 0) return null;

  return (
    <>
      <div className={classes.toolsBarTop}>
        {process.env.REACT_APP_SHOW_AED_ICON === "true" && (
          <>
            <IconButton onClick={() => setDrawerOpen(true)}>
              <Menu />
            </IconButton>
            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            >
              <div className={classes.list}>
                <FormControlLabel
                  className={classes.formControlLabel}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => setChecked(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <div style={{ display: "flex", alignItems: "center" }}>
                      AED <AedIcon style={{ marginLeft: 8 }} />
                    </div>
                  }
                />
              </div>
            </Drawer>
          </>
        )}
        <Paper component="form" className={classes.paper}>
          <Autocomplete
            onSelect={(e) => e.preventDefault()}
            options={listArray}
            getOptionLabel={(o) => o.room_name || o.building_name}
            value={searchValue}
            renderOption={(o) => (
              <div>
                <span className={classes.room_name_tag}>
                  {o.room_name || o.building_name}
                </span>
                <br />
                <span className={classes.building_name_tag}>
                  {o.building_name}
                </span>
                {o.floor_name && (
                  <span className={classes.floor_name_tag}>{o.floor_name}</span>
                )}
              </div>
            )}
            onChange={(_, value, reason) => {
              setSearchValue(value);
              if (reason === "select-option" && value) {
                handleClick(
                  value.room_id || value.building_base,
                  Boolean(value.isBuilding)
                );
                document.activeElement?.blur();
              } else if (reason === "clear" || (reason === "input" && !value)) {
                clearSearchPin();
              }
            }}
            noOptionsText="該当なし"
            style={{ width: "100%" }}
            filterOptions={(options, { inputValue }) => {
              const keyword = inputValue.toLowerCase();
              return options
                .filter(({ room_name = "", building_name = "" }) => {
                  const room = room_name.toLowerCase();
                  const building = building_name.toLowerCase();
                  return room.includes(keyword) || building.includes(keyword);
                })
                .slice(0, 10);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="検索"
                onKeyPress={(e) => {
                  if (e.key === "Enter") e.preventDefault();
                }}
                InputLabelProps={{ shrink: false }}
                variant="outlined"
              />
            )}
          />
        </Paper>
      </div>

      <div className={classes.toolsBarLeft}>
        <div className={classes.roomControl}>{children}</div>
      </div>

      <div className={classes.toolsBarRight}>
        <div className={classes.zoomControl}>
          {useGps && (
            <Fab
              aria-label="gps"
              size="small"
              color="secondary"
              onClick={handleCenterOnGps}
            >
              <MyLocationIcon />
            </Fab>
          )}
          <Fab
            aria-label="add"
            size="small"
            color="secondary"
            onClick={handleZoomIn}
          >
            <AddIcon />
          </Fab>
          <Fab
            aria-label="remove"
            size="small"
            color="secondary"
            onClick={handleZoomOut}
          >
            <RemoveIcon />
          </Fab>
        </div>

        <div className={classes.campusControl}>
          <FormControl>
            <Select
              classes={{ outlined: classes.select }}
              labelId="select-outlined-label"
              id="select-outlined"
              variant="outlined"
              value={currentCampus}
              autoWidth
              IconComponent={ArrowDropUpSharp}
              onChange={(e) => {
                changeCampus(e.target.value);
              }}
            >
              {dataJson.map((c, i) => (
                <MenuItem key={c.code} value={i + 1}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
    </>
  );
}

export default React.memo(MapToolbar);
